import {
  AsYouType,
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';

export interface PhoneCountry {
  code: CountryCode;
  name: string;
  dialCode: string;
  flag: string;
}

const countryName = (code: CountryCode): string => {
  try {
    const display = new Intl.DisplayNames(['fr'], { type: 'region' });
    return display.of(code) ?? code;
  } catch {
    return code;
  }
};

const codePointOffset = 0x1f1a5;
const flagEmoji = (code: CountryCode): string => {
  return String.fromCodePoint(...code.split('').map((c) => c.charCodeAt(0) + codePointOffset));
};

const PREFERRED: CountryCode[] = ['FR', 'BE', 'CH', 'LU', 'DE', 'ES', 'IT', 'GB', 'PT', 'NL', 'US', 'CA'];

export const COUNTRIES: PhoneCountry[] = (() => {
  const all: PhoneCountry[] = getCountries().map((code) => ({
    code,
    name: countryName(code),
    dialCode: `+${getCountryCallingCode(code)}`,
    flag: flagEmoji(code),
  }));
  const preferred = PREFERRED.map((c) => all.find((x) => x.code === c)).filter(Boolean) as PhoneCountry[];
  const rest = all
    .filter((c) => !PREFERRED.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return [...preferred, ...rest];
})();

export const DEFAULT_COUNTRY: CountryCode = 'FR';

/**
 * Detect the country from a stored E.164 value (e.g. "+33606060606").
 * Returns undefined if the value can't be parsed.
 */
export function detectCountry(value: string | null | undefined): CountryCode | undefined {
  if (!value) return undefined;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.country;
}

/**
 * Given a raw user input and a selected country, return:
 *  - display: the live-formatted national number (what to show in the input)
 *  - e164: the canonical international format to store (e.g. "+33606060606"), or "" if not parsable yet
 *  - country: the resolved country (may differ from input if user pasted an international number)
 */
export function parsePhoneInput(
  input: string,
  country: CountryCode,
): { display: string; e164: string; country: CountryCode } {
  const trimmed = input.trim();

  // Paste / typed international format → let libphonenumber detect country
  if (trimmed.startsWith('+') || trimmed.startsWith('00')) {
    const normalized = trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
    const parsed = parsePhoneNumberFromString(normalized);
    if (parsed?.country) {
      return {
        display: parsed.formatNational(),
        e164: parsed.number,
        country: parsed.country,
      };
    }
    // Keep raw input if not yet parseable (user still typing)
    return { display: trimmed, e164: '', country };
  }

  // National format for the selected country
  const formatter = new AsYouType(country);
  const display = formatter.input(trimmed);
  const parsed = parsePhoneNumberFromString(trimmed, country);
  return {
    display,
    e164: parsed?.isValid() ? parsed.number : '',
    country,
  };
}

/**
 * Format a stored E.164 value for display in the input when the component mounts
 * or receives a new external value.
 */
export function formatForDisplay(value: string | null | undefined, fallbackCountry: CountryCode): string {
  if (!value) return '';
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) return parsed.formatNational();
  // Not a valid E.164 yet — format as the fallback country
  return new AsYouType(fallbackCountry).input(value);
}

/**
 * Build the E.164 value from a national display string (used when the user only
 * changed the country dropdown).
 */
export function nationalToE164(display: string, country: CountryCode): string {
  if (!display.trim()) return '';
  const parsed = parsePhoneNumberFromString(display, country);
  return parsed?.isValid() ? parsed.number : '';
}

/**
 * Validate a stored phone value (E.164 or any parseable format). Empty → true
 * because phone is usually optional at validator level.
 */
export function isValidPhone(value: string | null | undefined): boolean {
  if (!value) return true;
  return isValidPhoneNumber(value);
}

/**
 * Format a stored E.164 value into a readable international string for display
 * lists: "+33 6 06 06 06 06".
 */
export function formatPhoneInternational(value: string | null | undefined): string {
  if (!value) return '-';
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) return parsed.formatInternational();
  return value;
}
